import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  output,
} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { IssueStatuses, NotificationTypes } from '@app/shared/enums';
import {
  FparTableActions,
  FparTableSelectors,
  NotificationActions,
} from '@app/store';
import {
  FparIssueComment,
  FparTableCellIssue,
  FparTableIssue,
  FparTableIssueCommentPayload,
} from '@core/models';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { BannerComponent } from '../banner/banner.component';
import { FparCommentComponent } from '../fpar-comment/fpar-comment.component';
import { FparSubIssueComponent } from '../fpar-sub-issue/fpar-sub-issue.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    BannerComponent,
    CommonModule,
    FparCommentComponent,
    FparSubIssueComponent,
    MatChipsModule,
    MatIconModule,
  ],
  selector: 'app-fpar-issue',
  templateUrl: './fpar-issue.component.html',
  styleUrls: ['./fpar-issue.component.scss'],
  standalone: true,
})
export class FparIssueComponent implements OnDestroy {
  public canResolve = input.required<boolean>();
  public grant = input.required<{ grantId: number; tableId: number }>();
  public index = input.required<number>();
  public issue = input.required<FparTableIssue>();
  public readOnly = input.required<boolean>();
  public systemForcedStatusChange = input.required<boolean>();
  public reportingPeriodId = input.required<number | null>();

  public activateSubIssue = output<FparTableCellIssue>();
  public editingComments = output<boolean>();
  public hold = output<FparTableIssue>();
  public resolve = output<FparTableIssue>();

  public actions$ = inject(Actions);
  public store$ = inject(Store);
  public hasComments = false;
  public issueStatus: IssueStatuses = IssueStatuses.None;
  public issueComments$: Observable<FparIssueComment[]>;
  public notificationTypes = NotificationTypes;
  public saving$: Observable<boolean>;

  private destroyed$ = new Subject<boolean>();

  constructor() {
    this.actions$
      .pipe(
        ofType(
          FparTableActions.addTableIssueCommentFailure,
          FparTableActions.updateTableIssueCommentFailure,
          FparTableActions.deleteTableIssueCommentFailure,
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe(({ message }) =>
        this.store$.dispatch(
          NotificationActions.add({
            notificationType: NotificationTypes.DANGER,
            notificationText: message,
          }),
        ),
      );

    this.actions$
      .pipe(
        ofType(
          FparTableActions.holdTableIssueSuccess,
          FparTableActions.addTableIssueCommentSuccess,
          FparTableActions.updateTableIssueCommentSuccess,
          FparTableActions.deleteTableIssueCommentSuccess,
        ),
        takeUntil(this.destroyed$),
        tap(() => {
          this.fetchTableIssueComments(
            this.grant().grantId,
            this.grant().tableId,
            this.issue().checkId,
            this.reportingPeriodId(),
          );
        }),
      )
      .subscribe();

    this.saving$ = combineLatest([
      this.store$.select(
        FparTableSelectors.selectUpdateTableIssueCommentLoading,
      ),
      this.store$.select(FparTableSelectors.selectAddTableIssueCommentLoading),
      this.store$.select(
        FparTableSelectors.selectDeleteTableIssueCommentLoading,
      ),
    ]).pipe(
      map((loading) => loading.some((l) => l)),
      takeUntil(this.destroyed$),
    );

    this.issueComments$ = this.actions$.pipe(
      ofType(FparTableActions.getTableIssueCommentsSuccess),
      filter((action) => action.response.checkId === this.issue().checkId),
      map((action) => action.response.apiResponse.data),
      takeUntil(this.destroyed$),
      tap((comments) => {
        this.hasComments = comments.length > 0;

        this.issueStatus = this.issue().resolved
          ? IssueStatuses.Resolved
          : this.hasComments
            ? IssueStatuses.OnHold
            : IssueStatuses.Unresolved;
      }),
    );

    effect(() => {
      this.fetchTableIssueComments(
        this.grant().grantId,
        this.grant().tableId,
        this.issue().checkId,
        this.reportingPeriodId(),
      );
    });
  }

  public get IssueStatuses() {
    return IssueStatuses;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public forcedSystemUpdateNoComments() {
    return (
      this.systemForcedStatusChange() && !this.hasComments && this.canResolve()
    );
  }

  public deleteComment(
    grantId: number,
    tableId: number,
    checkId: string,
    commentId: number,
  ): void {
    this.store$.dispatch(
      FparTableActions.deleteTableIssueComment({
        grantId,
        tableId,
        checkId,
        commentId,
      }),
    );
  }

  public onActivateSubIssue(subIssue: FparTableCellIssue): void {
    this.activateSubIssue.emit(subIssue);
  }

  public saveComment(
    grantId: number,
    tableId: number,
    checkId: string,
    payload: FparTableIssueCommentPayload,
  ): void {
    const commentId = payload.id;
    const action =
      payload.parentCommentId || !commentId
        ? FparTableActions.addTableIssueComment({
            grantId,
            tableId,
            checkId,
            payload,
          })
        : FparTableActions.updateTableIssueComment({
            grantId,
            tableId,
            checkId,
            commentId,
            payload,
          });
    this.store$.dispatch(action);
  }

  public setEditingComment(value: boolean): void {
    this.editingComments.emit(value);
  }

  public setOnHold(): void {
    this.hold.emit(this.issue());
  }

  public setResolved(): void {
    this.resolve.emit(this.issue());
  }

  private fetchTableIssueComments(
    grantId: number,
    tableId: number,
    checkId: string,
    reportingPeriodId: number | null,
  ): void {
    this.store$.dispatch(
      FparTableActions.getTableIssueComments({
        grantId,
        tableId,
        checkId,
        reportingPeriodId,
      }),
    );
  }
}
